import { cx } from '@linaria/core';
import { forwardRef, useRef, useLayoutEffect, useState } from 'react';
import { MoveFocusInside } from 'react-focus-on';
import { TransitionGroup } from 'react-transition-group';
import { breakpoints } from '@ui/css';
import { useComponentInteractions } from '@utils/component-framework';
import { useMatchBreakpoint, assertRefObject, useFullscreenState, useChangeEffect, ShowFor, SizeTransition } from '@utils/react';
import '@i-ark/models';
import { useUpdateEntryLocationMutation } from '../state/mutations/entries/location.update.js';
import '@tanstack/react-query';
import { useCurrentPage } from '../state/hook.current-page.js';
import { iArkStore } from '../state/store.js';
import { transitionDurationMs, fullscreenButton, section, sheetTokens, navButtonTrack, navButton, wrapper, transition, contentWrapper } from './_content.style.js';
import { NavigationButton } from './atom.navigation-button.js';
import { usePreloadImages } from './hook.preload-images.js';
import { useShortcut } from './hook.use-shortcut.js';
import { SheetWrapper } from './organism.sheet/_sheet.js';
import { sheetAspectRatio } from './organism.sheet/_sheet.style.js';
import { useIArkFullscreenActions } from './organism.tools/hooks/useFullscreenActions.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';

const IArkContent = /*#__PURE__*/forwardRef(function App(props, externalRef) {
  const internalRef = useRef(null);
  const ref = externalRef ?? internalRef;
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  assertRefObject(ref);
  const interactions = useComponentInteractions();
  const entryKey = iArkStore.useSelector(state => state.entryKey);
  const dispatch = iArkStore.useDispatch();
  const updateLocation = useUpdateEntryLocationMutation(entryKey);
  const {
    page,
    index
  } = useCurrentPage(props.node.component.data, entryKey);
  const {
    fullscreenEnabled
  } = useFullscreenState();
  const isFullscreen = fullscreenEnabled || props.context === "lightbox";
  const {
    overlayId,
    rootSelector,
    ...componentProps
  } = props;
  const {
    handleFullscreenClick
  } = useIArkFullscreenActions({
    overlayId,
    rootSelector,
    componentProps
  });
  const zoom = iArkStore.useSelector(state => state.zoom);
  const prevIndex = useRef(index);
  useLayoutEffect(() => {
    return () => {
      prevIndex.current = index;
    };
  }, [index]);
  useChangeEffect(() => {
    if (page?.id) {
      updateLocation({
        pageId: page.id
      });
    }
  }, [page?.id]);
  const handleDeselect = () => {
    dispatch(iArkStore.actions.deselectItem());
  };
  useShortcut("Escape", handleDeselect);
  usePreloadImages(index);

  //
  // keep track of the task that we've currently completed a transition onto
  // (undefined for the initially rendered task, as we didn't "transition" to
  // it)
  //
  // we use this information in order to determine whether or not to enable
  // automatic focus update to the first input within the new task
  //
  const [transitionedTaskIndex, setTransitionedTaskIndex] = useState(undefined);
  useChangeEffect(() => {
    const timeout = window.setTimeout(() => {
      setTransitionedTaskIndex(index);
    }, transitionDurationMs);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [index]);
  if (!page) {
    return null;
  }
  const transitionDirection = index > prevIndex.current ? "forwards" : "backwards";
  return /*#__PURE__*/jsxs(Fragment, {
    children: [!isFullscreen && /*#__PURE__*/jsx(ShowFor, {
      breakpoint: breakpoints.mobile,
      children: /*#__PURE__*/jsx("button", {
        "aria-label": "Aktiv\xE9r komponent",
        className: fullscreenButton,
        onClick: handleFullscreenClick
      })
    }), /*#__PURE__*/jsxs("div", {
      ref: ref,
      className: cx(section, transitionDirection),
      onMouseDown: handleDeselect,
      onTouchStart: handleDeselect,
      onClick: interactions.onActivate,
      style: sheetTokens.zoom.__override(zoom),
      "data-context": props.context,
      "data-fullscreen": isFullscreen,
      "aria-hidden": isMobile && !isFullscreen,
      children: [!isFullscreen && /*#__PURE__*/jsx("span", {
        className: cx(navButtonTrack, "left"),
        children: /*#__PURE__*/jsx("span", {
          className: cx(navButton, "left"),
          children: /*#__PURE__*/jsx(NavigationButton, {
            direction: "backwards",
            component: props.node.component
          })
        })
      }), /*#__PURE__*/jsxs(TransitionGroup, {
        className: wrapper,
        children: [!isFullscreen && /*#__PURE__*/jsx(SizeTransition, {
          axis: "vertical",
          swappable: true,
          retainClassnamesOnExit: true,
          timeout: transitionDurationMs,
          classNames: transition,
          delayMs: {
            enter: 10,
            exit: 10
          },
          children: ref => /*#__PURE__*/jsx(TransitionContent, {
            ref: ref,
            ...props,
            index: index,
            page: page,
            autoFocus: index === transitionedTaskIndex
          })
        }, index), isFullscreen && /*#__PURE__*/jsx(SizeTransition, {
          axis: "both",
          swappable: true,
          retainClassnamesOnExit: true,
          timeout: transitionDurationMs,
          classNames: transition,
          delayMs: {
            enter: 10,
            exit: 10
          },
          children: ref => /*#__PURE__*/jsx(TransitionContent, {
            ref: ref,
            ...props,
            index: index,
            page: page,
            autoFocus: index === transitionedTaskIndex
          })
        }, index)]
      }), !isFullscreen && /*#__PURE__*/jsx("span", {
        className: cx(navButtonTrack, "right"),
        children: /*#__PURE__*/jsx("span", {
          className: cx(navButton, "right"),
          children: /*#__PURE__*/jsx(NavigationButton, {
            direction: "forwards",
            component: props.node.component
          })
        })
      })]
    })]
  });
});
const TransitionContent = /*#__PURE__*/forwardRef(function TransitionContent({
  index,
  page,
  autoFocus,
  ...props
}, ref) {
  const AutoFocusIfNeeded = autoFocus ? MoveFocusInside : "div";
  return /*#__PURE__*/jsx("div", {
    ref: ref,
    className: transition,
    style: sheetAspectRatio.__override(page.aspectRatio),
    children: /*#__PURE__*/jsx(AutoFocusIfNeeded, {
      children: /*#__PURE__*/jsx("div", {
        className: contentWrapper,
        children: /*#__PURE__*/jsx(SheetWrapper, {
          defaultInputColor: props.node.component.data.style.colors.inputTextColor ?? "#000",
          defaultTextColor: props.node.component.data.style.colors.textColor ?? "#000",
          page: page,
          context: props.context
        }, index)
      })
    })
  });
});

export { IArkContent };
//# sourceMappingURL=_content.js.map
